
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import * as XLSX from 'xlsx';

interface FormValues {
    branch_id: string;
    status: string;
    category: string;
    brand: string;
    model: string;
    manufacture_year: number;
    color: string;
    registration_number: string;
    registration_expiry_date: string;
    vin_chassis_number: string;
    assigned_driver_id: string;
    purchase_date: string;
    purchase_cost: number;
    next_service_due: string;
    insurance_provider: string;
    insurance_policy_number: string;
    insurance_expiry_date: string;


    // Owner Details
    owner_name: string;
    owner_mobile: string;
    owner_email: string;

    // Files
    vehicle_image: FileList;
    rc_document: FileList;
    fuel_type: string;
    odometer_reading: number;
    driver_assigned_at: string;
}

interface Option {
    id: number;
    name?: string;
    branch_name?: string;
    assigned_branch_id?: string | number;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddVehicle() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<Option[]>([]);
    const [drivers, setDrivers] = useState<Option[]>([]);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors }, reset
    } = useForm<FormValues>();

    const selectedBranchId = watch("branch_id");

    const fetchDriversForBranch = async (branchId: string) => {
        try {
            const response: any = await apiPost("drivers/list", { branch_id: branchId });
            if (Array.isArray(response)) {
                setDrivers(response);
            } else if (response.data) {
                setDrivers(response.data);
            } else {
                setDrivers([]);
            }
        } catch (error) {
            console.error("Error fetching drivers:", error);
            setDrivers([]);
        }
    };

    // Fetch drivers and reset assigned_driver when branch changes
    useEffect(() => {
        if (selectedBranchId) {
            fetchDriversForBranch(selectedBranchId);
            setValue("assigned_driver_id", "");
        } else {
            setDrivers([]);
            setValue("assigned_driver_id", "");
        }
    }, [selectedBranchId, setValue]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const branchRes = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchRes);
            } catch (error) {
                console.error("Failed to fetch options", error);
            }
        };
        fetchOptions();
    }, []);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            let vehicleImageName = "";
            let rcDocName = "";

            if (data.vehicle_image && data.vehicle_image[0]) {
                vehicleImageName = await uploadFile(data.vehicle_image[0]);
            }
            if (data.rc_document && data.rc_document[0]) {
                rcDocName = await uploadFile(data.rc_document[0]);
            }

            const payload = {
                ...data,
                vehicle_image: vehicleImageName,
                rc_document: rcDocName
            };

            await apiPost("vehicles/store", payload);
            toast.success("Vehicle added successfully", { autoClose: 1000 });
            reset();
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/vehicle`);
            }, 1000);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || "An unexpected error occurred";
            toast.error(errorMessage, { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    };

    const [isUploading, setIsUploading] = useState(false);

    const downloadSampleExcel = () => {
        const headers = [
            "Status", "Category", "Brand", "Model",
            "Manufacture Year", "Color", "Fuel Type", "Registration Number",
            "Registration Expiry Date", "VIN/Chassis Number", "Purchase Date",
            "Purchase Cost", "Next Service Due", "Insurance Provider",
            "Insurance Policy Number", "Insurance Expiry Date",
            "Owner Name", "Owner Mobile", "Owner Email"
        ];
        const rows = [
            [
                "Active", "Heavy Vehicle", "Toyota", "Camry",
                "2023", "White", "Petrol", "ABC-1234",
                "2025-12-31", "VIN123456789", "2023-01-01",
                "50000", "2024-01-01", "Allianz",
                "POL-123", "2025-12-31",
                "John Doe", "9876543210", "john@example.com"
            ],
            [
                "Inactive", "Light Vehicle", "Honda", "Civic",
                "2022", "Black", "Diesel", "XYZ-9876",
                "2026-06-30", "VIN987654321", "2022-05-15",
                "45000", "2024-05-15", "AXA",
                "POL-456", "2026-06-30",
                "Jane Doe", "9876543211", "johns@example.com"
            ]
        ];

        const worksheet = (XLSX as any).utils.aoa_to_sheet([headers, ...rows]);
        const workbook = (XLSX as any).utils.book_new();
        (XLSX as any).utils.book_append_sheet(workbook, worksheet, "Vehicles");
        (XLSX as any).writeFile(workbook, "vehicle_import_sample.xlsx");
    };

    const handleBulkImport = (e: React.ChangeEvent<HTMLInputElement>) => {

        if (e.target.files && e.target.files[0]) {
            setIsUploading(true);
            const file = e.target.files[0];

            const reader = new FileReader();
            reader.onload = async (evt) => {
                const arrayBuffer = evt.target?.result;
                const wb = (XLSX as any).read(arrayBuffer, { type: 'array' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data: any[][] = (XLSX as any).utils.sheet_to_json(ws, { header: 1, raw: false });

                const validationErrors: string[] = [];
                if (data.length <= 1) {
                    toast.error("File appears to be empty or contains only headers.");
                    e.target.value = '';
                    setIsUploading(false);
                    return;
                }

                // Updated indices after removing Branch ID (Status is now 0)
                const requiredColumns = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
                const columnNames = [
                    "Status", "Category", "Brand", "Model",
                    "Year", "Color", "Fuel", "Reg No", "Reg Expiry",
                    "VIN", "Purchase Date", "Cost", "Next Service",
                    "Ins Provider", "Policy No", "Ins Expiry",
                    "Owner Name", "Owner Mobile", "Owner Email"
                ];

                const vehiclesToImport: any[] = [];
                const regNos = new Set();
                const vins = new Set();

                // Loop rows (skip header)
                for (let i = 1; i < data.length; i++) {
                    const row = data[i];
                    if (!row || row.length === 0) continue;

                    let rowError = false;
                    const checkRequired = (val: any) => val !== undefined && val !== null && val.toString().trim() !== "";

                    requiredColumns.forEach((colIndex) => {
                        if (!checkRequired(row[colIndex])) {
                            validationErrors.push(`Row ${i + 1}: ${columnNames[colIndex]} is required`);
                            rowError = true;
                        }
                    });

                    // Basic Date Format Validation for date columns
                    // Reg Expiry: 8, Purchase: 10, Next Service: 12, Ins Expiry: 15
                    [8, 10, 12, 15].forEach((colIndex) => {
                        if (checkRequired(row[colIndex]) && !/^\d{4}-\d{2}-\d{2}$/.test(row[colIndex].toString().trim())) {
                            validationErrors.push(`Row ${i + 1}: ${columnNames[colIndex]} must be YYYY-MM-DD`);
                            rowError = true;
                        }
                    });

                    // Unique Checks within file
                    const regNo = row[7];
                    if (regNo && regNos.has(regNo)) {
                        validationErrors.push(`Row ${i + 1}: Duplicate Registration Number '${regNo}' in file`);
                        rowError = true;
                    } else if (regNo) {
                        regNos.add(regNo);
                    }

                    const vin = row[9];
                    if (vin && vins.has(vin)) {
                        validationErrors.push(`Row ${i + 1}: Duplicate VIN '${vin}' in file`);
                        rowError = true;
                    } else if (vin) {
                        vins.add(vin);
                    }

                    if (!rowError) {
                        vehiclesToImport.push({
                            branch_id: selectedBranchId || null, // Inject selected branch or null
                            status: row[0],
                            category: row[1],
                            brand: row[2],
                            model: row[3],
                            manufacture_year: row[4],
                            color: row[5],
                            fuel_type: row[6],
                            registration_number: row[7],
                            registration_expiry_date: row[8],
                            vin_chassis_number: row[9],
                            purchase_date: row[10],
                            purchase_cost: row[11],
                            next_service_due: row[12],
                            insurance_provider: row[13],
                            insurance_policy_number: row[14],
                            insurance_expiry_date: row[15],
                            owner_name: row[16],
                            owner_mobile: row[17],
                            owner_email: row[18]
                        });
                    }
                }

                if (validationErrors.length > 0) {
                    toast.error("Missing fields detected in the Excel file. Please check your data.");
                    e.target.value = '';
                    setIsUploading(false);
                    return;
                }

                try {
                    const response: any = await apiPost("vehicles/import", { vehicles: vehiclesToImport });
                    toast.success(response.message || "Vehicles imported successfully");
                    navigate(`${process.env.PUBLIC_URL}/vehicle`);
                } catch (error: any) {
                    const errMsg = error.response?.data?.message || error.message || "Import failed";
                    toast.error(errMsg);
                } finally {
                    e.target.value = '';
                    setIsUploading(false);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Add Vehicle" parent="General" title="Add Vehicle" />
            <Container fluid>
                <Card>
                    <CardBody className="custom-input form-validation">
                        <Row className="mb-4">
                            <Col md={8}>
                                <div className="mb-3">
                                    <h5>Import Instructions:</h5>
                                    <ul className="list-group list-group-flush small">
                                        <li className="list-group-item py-1">1. Excel format: <strong>Status, Category, Brand, Model, Year, Color, Fuel, Reg No, Reg Expiry(YYYY-MM-DD), VIN, Purchase Date(YYYY-MM-DD), Cost, Next Service(YYYY-MM-DD), Insurance Info (Provider, Policy No, Expiry), Owner Info (Name, Contact, Email)</strong>.</li>
                                        <li className="list-group-item py-1">2. <strong>Status</strong>: Active, Inactive, Maintenance | <strong>Category</strong>: Heavy Vehicle, Light Vehicle, Two Wheeler | <strong>Fuel Type</strong>: Petrol, Diesel, Electric, Hybrid, CNG.</li>
                                        <li className="list-group-item py-1">3. <strong>Dates</strong> must be YYYY-MM-DD (e.g., 2025-12-31).</li>
                                        <li className="list-group-item py-1">4. <strong>Reg No</strong> and <strong>VIN</strong> must be unique and not already exist in the system.</li>
                                        <li className="list-group-item py-1">5. All fields in the sample are <strong>mandatory</strong>.</li>
                                        <li className="list-group-item py-1">6. Phone numbers must include the country code (e.g., +919876543211, +971516144983, +966597058094).</li>
                                    </ul>
                                </div>
                            </Col>
                            <Col md={4} className="d-flex align-items-center justify-content-end gap-2">
                                <Btn color="success" size="sm" onClick={downloadSampleExcel} outline>
                                    Download Sample Excel
                                </Btn>
                                <div style={{ position: 'relative', overflow: 'hidden', display: 'inline-block' }}>
                                    <Btn color="info" size="sm" outline disabled={isUploading}>
                                        {isUploading ? "Uploading..." : "Upload Excel"}
                                    </Btn>
                                    <input
                                        type="file"
                                        accept=".xlsx, .xls"
                                        onChange={handleBulkImport}
                                        disabled={isUploading}
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            opacity: 0,
                                            width: '100%',
                                            height: '100%',
                                            cursor: isUploading ? 'not-allowed' : 'pointer'
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Vehicle Information</H5>
                            <hr />
                            <Row className="g-3 mb-4">


                                <Col md={3}>
                                    <Label>Brand <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="brand"
                                        control={control}
                                        rules={{ required: "Brand is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Toyota" invalid={!!errors.brand} style={{ borderColor: errors.brand ? "red" : "" }} autoComplete="off" />
                                                <FormFeedback>{errors.brand?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Model <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="model"
                                        control={control}
                                        rules={{ required: "Model is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Camry" invalid={!!errors.model} style={{ borderColor: errors.model ? "red" : "" }} autoComplete="off" />
                                                <FormFeedback>{errors.model?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Manufacture Year <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="manufacture_year"
                                        control={control}
                                        rules={{
                                            required: "Manufacture Year is required",
                                            min: { value: 1900, message: "Year must be 1900 or later" },
                                            max: { value: new Date().getFullYear(), message: `Year must be ${new Date().getFullYear()} or earlier` }
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder="2023" invalid={!!errors.manufacture_year} style={{ borderColor: errors.manufacture_year ? "red" : "" }} autoComplete="off" />
                                                <FormFeedback>{errors.manufacture_year?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Color <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="color"
                                        control={control}
                                        rules={{ required: "Color is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="White" invalid={!!errors.color} style={{ borderColor: errors.color ? "red" : "" }} autoComplete="off" />
                                                <FormFeedback>{errors.color?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Fuel Type <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="fuel_type"
                                        control={control}
                                        rules={{ required: "Fuel Type is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.fuel_type} style={{ borderColor: errors.fuel_type ? "red" : "" }} autoComplete="off">
                                                    <option value="">Select Fuel Type</option>
                                                    <option value="Petrol">Petrol</option>
                                                    <option value="Diesel">Diesel</option>
                                                    <option value="Electric">Electric</option>
                                                    <option value="Hybrid">Hybrid</option>
                                                    <option value="CNG">CNG</option>
                                                </Input>
                                                <FormFeedback>{errors.fuel_type?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id} style={{ borderColor: errors.branch_id ? "red" : "" }} autoComplete="off">
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Status <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{ required: "Status is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.status} style={{ borderColor: errors.status ? "red" : "" }} autoComplete="off">
                                                    <option value="">Select Status</option>
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                    <option value="Maintenance">Maintenance</option>
                                                    <option value="Destroyed">Destroyed</option>
                                                    {/* <option value="Sold">Sold</option> */}
                                                </Input>
                                                <FormFeedback>{errors.status?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Category <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="category"
                                        control={control}
                                        rules={{ required: "Category is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.category} style={{ borderColor: errors.category ? "red" : "" }} autoComplete="off">
                                                    <option value="">Select Category</option>
                                                    <option value="Heavy Vehicle">Heavy Vehicle</option>
                                                    <option value="Light Vehicle">Light Vehicle</option>
                                                    <option value="Two Wheeler">Two Wheeler</option>
                                                </Input>
                                                <FormFeedback>{errors.category?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Registration Number <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="registration_number"
                                        control={control}
                                        rules={{ required: "Reg No is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="ABC-1234" invalid={!!errors.registration_number} style={{ borderColor: errors.registration_number ? "red" : "" }} autoComplete="off" />
                                                <FormFeedback>{errors.registration_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Registration Expiry <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="registration_expiry_date"
                                        control={control}
                                        rules={{ required: "Expiry Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.registration_expiry_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                    placeholderText="YYYY-MM-DD"
                                                    dateFormat="yyyy-MM-dd"
                                                    // minDate={new Date()}
                                                    peekNextMonth
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    autoComplete="off"
                                                />
                                                <FormFeedback className={errors.registration_expiry_date ? "d-block" : ""}>{errors.registration_expiry_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>VIN / Chassis <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vin_chassis_number"
                                        control={control}
                                        rules={{ required: "VIN is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="VIN123..." invalid={!!errors.vin_chassis_number} style={{ borderColor: errors.vin_chassis_number ? "red" : "" }} autoComplete="off" />
                                                <FormFeedback>{errors.vin_chassis_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Assigned Driver</Label>
                                    <Controller
                                        name="assigned_driver_id"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.assigned_driver_id} style={{ borderColor: errors.assigned_driver_id ? "red" : "" }} autoComplete="off">
                                                    <option value="">Select Driver</option>
                                                    {drivers.map((d: any) => (
                                                        <option key={d.id} value={d.id}>{d.name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.assigned_driver_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                {selectedBranchId && watch("assigned_driver_id") && (
                                    <>
                                        <Col md={6}>
                                            <Label>Assign Time <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="driver_assigned_at"
                                                control={control}
                                                rules={{ required: "Assign Time is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <DatePicker
                                                            className={`form-control ${errors.driver_assigned_at ? "is-invalid" : ""}`}
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? date.toISOString() : "")}
                                                            placeholderText="Select Date & Time"
                                                            showTimeSelect
                                                            dateFormat="yyyy-MM-dd HH:mm"
                                                            autoComplete="off"
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            dropdownMode="select"
                                                        />
                                                        <FormFeedback className={errors.driver_assigned_at ? "d-block" : ""}>{errors.driver_assigned_at?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Odometer Reading <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="odometer_reading"
                                                control={control}
                                                rules={{ required: "Odometer Reading is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="number" {...field} placeholder="e.g. 15000" invalid={!!errors.odometer_reading} style={{ borderColor: errors.odometer_reading ? "red" : "" }} autoComplete="off" />
                                                        <FormFeedback>{errors.odometer_reading?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                    </>
                                )}
                            </Row>

                            <Col xs={12}>
                                <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                    <CardHeader tag="h5" style={{
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                    }}>
                                        Purchase & Service
                                    </CardHeader>
                                    <CardBody>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Label>Purchase Date <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="purchase_date"
                                                    control={control}
                                                    rules={{ required: "Purchase Date is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <DatePicker
                                                                className={`form-control ${errors.purchase_date ? "is-invalid" : ""}`}
                                                                selected={field.value ? new Date(field.value) : null}
                                                                onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                                placeholderText="YYYY-MM-DD"
                                                                dateFormat="yyyy-MM-dd"
                                                                peekNextMonth
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                dropdownMode="select"
                                                                autoComplete="off"
                                                            />
                                                            <FormFeedback className={errors.purchase_date ? "d-block" : ""}>{errors.purchase_date?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label>Purchase Cost <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="purchase_cost"
                                                    control={control}
                                                    rules={{ required: "Purchase Cost is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Input type="number" {...field} placeholder="0.00" invalid={!!errors.purchase_cost} style={{ borderColor: errors.purchase_cost ? "red" : "" }} autoComplete="off" />
                                                            <FormFeedback>{errors.purchase_cost?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <Label>Next Service Due <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="next_service_due"
                                                    control={control}
                                                    rules={{ required: "Next Service Date is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <DatePicker
                                                                className={`form-control ${errors.next_service_due ? "is-invalid" : ""}`}
                                                                selected={field.value ? new Date(field.value) : null}
                                                                onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                                placeholderText="YYYY-MM-DD"
                                                                dateFormat="yyyy-MM-dd"
                                                                // minDate={new Date()}
                                                                peekNextMonth
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                dropdownMode="select"
                                                                autoComplete="off"
                                                            />
                                                            <FormFeedback className={errors.next_service_due ? "d-block" : ""}>{errors.next_service_due?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>



                            <Col xs={12}>
                                <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                    <CardHeader tag="h5" style={{
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                    }}>
                                        Insurance Details
                                    </CardHeader>
                                    <CardBody>
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <Label>Insurance Provider <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="insurance_provider"
                                                    control={control}
                                                    rules={{ required: "Provider is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Input {...field} placeholder="Provider Name" invalid={!!errors.insurance_provider} style={{ borderColor: errors.insurance_provider ? "red" : "" }} autoComplete="off" />
                                                            <FormFeedback>{errors.insurance_provider?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Label>Policy Number <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="insurance_policy_number"
                                                    control={control}
                                                    rules={{ required: "Policy Number is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Input {...field} placeholder="Policy Number" invalid={!!errors.insurance_policy_number} style={{ borderColor: errors.insurance_policy_number ? "red" : "" }} autoComplete="off" />
                                                            <FormFeedback>{errors.insurance_policy_number?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Label>Insurance Expiry <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="insurance_expiry_date"
                                                    control={control}
                                                    rules={{ required: "Expiry Date is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <DatePicker
                                                                className={`form-control ${errors.insurance_expiry_date ? "is-invalid" : ""}`}
                                                                selected={field.value ? new Date(field.value) : null}
                                                                onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                                placeholderText="YYYY-MM-DD"
                                                                dateFormat="yyyy-MM-dd"
                                                                // minDate={new Date()}
                                                                peekNextMonth
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                dropdownMode="select"
                                                                autoComplete="off"
                                                            />
                                                            <FormFeedback className={errors.insurance_expiry_date ? "d-block" : ""}>{errors.insurance_expiry_date?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xs={12}>
                                <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                    <CardHeader tag="h5" style={{
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                    }}>
                                        Owner Details
                                    </CardHeader>
                                    <CardBody>
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <Label>Owner Name <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="owner_name"
                                                    control={control}
                                                    rules={{ required: "Owner Name is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Input {...field} placeholder="Owner Name" invalid={!!errors.owner_name} style={{ borderColor: errors.owner_name ? "red" : "" }} autoComplete="off" />
                                                            <FormFeedback>{errors.owner_name?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Label>Owner Contact <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="owner_mobile"
                                                    control={control}
                                                    rules={{ required: "Owner Contact is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <PhoneInput
                                                                country={"in"}
                                                                onlyCountries={["in", "sa", "ae"]}
                                                                disableDropdown={false}
                                                                countryCodeEditable={false}
                                                                value={field.value}
                                                                onChange={(value) => field.onChange(value)}
                                                                inputProps={{
                                                                    name: "owner_mobile",
                                                                    placeholder: "Owner Contact",
                                                                    autoComplete: "none",
                                                                }}
                                                                inputStyle={{
                                                                    backgroundColor: "var(--input-background-color)",
                                                                    color: "var(--input-text-color)",
                                                                    width: "100%",
                                                                    borderColor: errors.owner_mobile ? "red" : "var(--input-border-color, #e0e0e0)",
                                                                }}
                                                                dropdownStyle={{
                                                                    backgroundColor: "var(--input-background-color)",
                                                                    backdropFilter: "blur(1000px)",
                                                                }}
                                                                buttonStyle={{
                                                                    backgroundColor: "var(--input-background-color)",
                                                                }}
                                                                specialLabel=""
                                                            />
                                                            {errors.owner_mobile && <small className="text-danger">{errors.owner_mobile.message}</small>}
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Label>Owner Email <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="owner_email"
                                                    control={control}
                                                    rules={{ required: "Owner Email is required", pattern: { value: /^\S+@\S+$/i, message: "Invalid email format" } }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Input type="email" {...field} placeholder="Owner Email" invalid={!!errors.owner_email} style={{ borderColor: errors.owner_email ? "red" : "" }} autoComplete="off" />
                                                            <FormFeedback>{errors.owner_email?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <Label>Vehicle Image <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_image"
                                        control={control}
                                        rules={{
                                            required: "Vehicle Image is required",
                                            validate: (files: any) => {
                                                if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                    return "File size must be less than 2MB";
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*"
                                                    onChange={(e) => onChange(e.target.files)}
                                                    invalid={!!errors.vehicle_image}
                                                    style={{ borderColor: errors.vehicle_image ? "red" : "" }}
                                                    autoComplete="off"
                                                />
                                                <FormFeedback>{errors.vehicle_image?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>RC Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="rc_document"
                                        control={control}
                                        rules={{
                                            required: "RC Document is required",
                                            validate: (files: any) => {
                                                if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                    return "File size must be less than 2MB";
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*"
                                                    onChange={(e) => onChange(e.target.files)}
                                                    invalid={!!errors.rc_document}
                                                    style={{ borderColor: errors.rc_document ? "red" : "" }}
                                                    autoComplete="off"
                                                />
                                                <FormFeedback>{errors.rc_document?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Btn color="primary" disabled={loading} type="submit">
                                {loading ? <>Submiting <Spinner size="sm" /></> : "Submit"}
                            </Btn>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div >
    );
}
